import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { HeroSection } from "../components/HeroSection";
import { StatsBar } from "../components/StatsBar";
import { FilterBar } from "../components/FilterBar";
import { InstitutionTable } from "../components/InstitutionTable";
import { InstitutionDrawer } from "../components/InstitutionDrawer";
import { SiteFooter } from "../components/SiteFooter";
import { BrowseHub } from "../components/BrowseHub";
import { LandingIntro, LandingRelated } from "../components/LandingBlocks";
import {
  PrivateCourtBanner,
  PrivateCourtInline,
  PrivateCourtFooterStrip,
} from "../components/PrivateCourtAd";
import { Seo, slugify } from "../components/Seo";
import { fetchInstitutions, fetchMeta } from "../lib/api";
import { toast } from "sonner";

const deslugify = (s) => (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ------------------------------------------------------------------ */
/*  Build dynamic SEO payload for Home / Category / State landings.   */
/* ------------------------------------------------------------------ */
const buildSeo = ({ mode, value, total }) => {
  const SITE = "https://bankdesks.com";
  const countStr = total ? total.toLocaleString("en-IN") : "9,381";

  if (mode === "category") {
    const title = `${value} in India — Nodal, Grievance, Recovery & Legal Head Contacts`;
    const description =
      `Complete directory of every RBI-registered ${value} in India. ` +
      `Find Principal Nodal Officer, Grievance Redressal, Recovery Head and Legal Head contacts — ` +
      `email, phone and registered address. ${countStr} ${value} institutions indexed, updated regularly.`;
    const keywords = [
      `${value} india`,
      `${value} nodal officer`,
      `${value} grievance officer`,
      `${value} recovery head contact`,
      `${value} legal head contact`,
      `${value} customer care email`,
      `${value} RBI registered list`,
      `list of ${value} in india`,
      `${value} complaint email id`,
      `${value} head office contact`,
    ];
    const path = `/category/${slugify(value)}`;
    return {
      title,
      description,
      keywords,
      path,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          description,
          url: `${SITE}${path}`,
          inLanguage: "en-IN",
          isPartOf: { "@type": "WebSite", name: "BankDesks.com", url: SITE },
          about: { "@type": "Thing", name: value },
        },
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Categories", path: "/" },
        { name: value, path },
      ],
    };
  }

  if (mode === "state") {
    const title = `Banks & NBFCs in ${value} — Nodal, Recovery & Legal Head Contacts`;
    const description =
      `Every RBI-registered Bank & NBFC headquartered in ${value}. ` +
      `Principal Nodal Officer, Grievance Redressal, Recovery Head and Legal Head contact details ` +
      `including email, phone and registered address. ${countStr} institutions listed for ${value}.`;
    const keywords = [
      `banks in ${value}`,
      `nbfc in ${value}`,
      `${value} bank nodal officer`,
      `${value} nbfc grievance officer`,
      `${value} recovery head contact`,
      `${value} legal head contact`,
      `${value} bank complaint email`,
      `RBI registered banks ${value}`,
      `loan recovery ${value}`,
      `bank head office ${value}`,
    ];
    const path = `/state/${slugify(value)}`;
    return {
      title,
      description,
      keywords,
      path,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          description,
          url: `${SITE}${path}`,
          inLanguage: "en-IN",
          isPartOf: { "@type": "WebSite", name: "BankDesks.com", url: SITE },
          spatialCoverage: {
            "@type": "AdministrativeArea",
            name: value,
            containedInPlace: { "@type": "Country", name: "India" },
          },
        },
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "States", path: "/" },
        { name: value, path },
      ],
    };
  }

  // Home
  const title = "India Banks & NBFCs Contact Directory — Nodal, Grievance, Recovery & Legal Heads";
  const description =
    `India's most complete public directory of Banks & NBFCs. ` +
    `Find Principal Nodal Officer, Grievance Redressal, Recovery Head and Legal Head contact details ` +
    `— email, phone and registered address — for ${countStr} RBI-registered institutions. ` +
    `Free, searchable, updated from RBI disclosures.`;
  const keywords = [
    "bank nodal officer india",
    "nbfc grievance officer",
    "recovery head contact",
    "legal head bank contact",
    "RBI registered NBFC list",
    "bank customer care email",
    "NBFC complaint email id",
    "SARFAESI notice contact",
    "loan recovery escalation",
    "bank grievance redressal officer",
    "principal nodal officer list",
    "RBI ombudsman escalation",
    "india bank directory",
    "nbfc directory india",
    "BankDesks.com",
    "BankDesks",
  ];
  return {
    title,
    description,
    keywords,
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "BankDesks.com",
        alternateName: "India Banks & NBFCs Contact Directory",
        url: `${SITE}/`,
        description,
        inLanguage: "en-IN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "BankDesks.com",
        url: `${SITE}/`,
        logo: `${SITE}/favicon.ico`,
        areaServed: "IN",
        sameAs: [],
        description,
      },
    ],
    breadcrumbs: [{ name: "Home", path: "/" }],
  };
};

export default function Directory() {
  const { categorySlug, stateSlug } = useParams();
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [category, setCategory] = useState("All");
  const [state, setState] = useState("All");
  const [role, setRole] = useState("All");
  const [detailed, setDetailed] = useState("All");

  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, results: [] });

  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(
    () => ({ q: q || undefined, type, category, state, role, detailed, page, page_size: pageSize }),
    [q, type, category, state, role, detailed, page]
  );

  useEffect(() => {
    fetchMeta().then((m) => {
      setMeta(m);
      // Preset filters from URL route params (category/state)
      if (categorySlug && m?.categories) {
        const match = m.categories.find(
          (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") === categorySlug
        );
        if (match) setCategory(match);
      }
      if (stateSlug && m?.states) {
        const match = m.states.find(
          (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") === stateSlug
        );
        if (match) setState(match);
      }
    }).catch(() => {});
  }, [categorySlug, stateSlug]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchInstitutions(filters)
        .then(setData)
        .catch(() => setData({ total: 0, results: [] }))
        .finally(() => setLoading(false));
    }, 200); // debounce
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => { setPage(1); }, [q, type, category, state, role, detailed]);

  const onSelect = (id) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const onReset = () => {
    setQ("");
    setType("All");
    setCategory("All");
    setState("All");
    setRole("All");
    setDetailed("All");
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const seoMode = categorySlug ? "category" : stateSlug ? "state" : "home";
  const seoValue = categorySlug
    ? category !== "All"
      ? category
      : deslugify(categorySlug)
    : stateSlug
      ? state !== "All"
        ? state
        : deslugify(stateSlug)
      : null;
  const seoPayload = useMemo(
    () => buildSeo({ mode: seoMode, value: seoValue, total: data.total }),
    [seoMode, seoValue, data.total]
  );

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <Seo
        title={seoPayload.title}
        description={seoPayload.description}
        keywords={seoPayload.keywords}
        path={seoPayload.path}
        jsonLd={seoPayload.jsonLd}
        breadcrumbs={seoPayload.breadcrumbs}
      />
      <SiteHeader />
      {/* On category/state landings we swap the marketing hero for a compact SEO-friendly intro */}
      {categorySlug || stateSlug ? (
        <LandingIntro
          mode={categorySlug ? "category" : "state"}
          value={seoValue}
          total={data.total}
        />
      ) : (
        <>
          <HeroSection />
          <StatsBar />
          <BrowseHub />
        </>
      )}

      <section id="directory" data-testid="directory-section" className="border-b border-[#0A0A0A] bg-[#F8F9FA]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-12">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
                Section 02 / Directory
              </div>
              <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter">
                Search Directory
              </h2>
            </div>
          </div>

          <FilterBar
            q={q} setQ={setQ}
            type={type} setType={setType}
            category={category} setCategory={setCategory}
            state={state} setState={setState}
            role={role} setRole={setRole}
            detailed={detailed} setDetailed={setDetailed}
            meta={meta}
            onReset={onReset}
            total={data.total}
          />

          <div className="mt-6">
            <PrivateCourtBanner />
          </div>

          <div className="mt-8">
            <InstitutionTable items={data.results} loading={loading} onSelect={onSelect} />
          </div>

          {totalPages > 1 && (
            <div data-testid="pagination" className="mt-6 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="pagination-prev"
                  className="border border-[#0A0A0A] px-4 py-2 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0A0A0A]"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="pagination-next"
                  className="border border-[#0A0A0A] px-4 py-2 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0A0A0A]"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          <div className="mt-8">
            <PrivateCourtInline />
          </div>
        </div>
      </section>

      {/* Related block on category / state landings */}
      {(categorySlug || stateSlug) && (
        <LandingRelated
          mode={categorySlug ? "category" : "state"}
          value={seoValue}
        />
      )}

      <PrivateCourtFooterStrip />

      <SiteFooter />

      <InstitutionDrawer id={selectedId} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
